import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, Clock, Check, AlertCircle } from 'lucide-react';
import { MindMapNode, YouTubeVideoEntry, Language } from '../types';
import { fetchYouTubeTranscript, fetchYouTubeVideoSnippet, updateYouTubeVideo } from '../services/youtubeService';
import { generateVideoChapters } from '../services/geminiService';
import { chapterState, formatChaptersForDescription, mergeChaptersIntoDescription, VideoChapter } from '../utils/youtubeChapters';

type Item<N> = { video: YouTubeVideoEntry; node: N };

type RowStatus =
    | { kind: 'idle' }
    | { kind: 'working' }
    | { kind: 'ready'; chapters: VideoChapter[] }
    | { kind: 'skipped'; reason: 'no_captions' | 'too_short' | 'own_list' | 'too_long' }
    | { kind: 'failed'; message: string }
    | { kind: 'applied' };

interface Props<N extends MindMapNode> {
    items: Item<N>[];
    vn: boolean;
    language: Language;
    ytConnected: boolean;
    onClose: () => void;
    /** Hidden while the batch runs in the background (kept mounted, so the run continues). */
    hidden?: boolean;
    onMinimize?: () => void;
    /** Reports whether a batch is running, for the background pill. */
    onBusyChange?: (busy: boolean) => void;
    /** Cache freshly fetched captions on the video (text + timed), as the single-video flow does. */
    onSaveTranscript: (node: N, video: YouTubeVideoEntry, text: string, timed: string) => Promise<void>;
    /** Record the description that is now live on YouTube. */
    onApplied: (node: N, video: YouTubeVideoEntry, description: string) => Promise<void>;
}

// videos.update / captions calls share one daily quota; two at a time keeps the run gentle.
const CONCURRENCY = 2;

/**
 * Adds a timestamp list (YouTube chapters) to many videos at once, in two explicit steps:
 * preview — build the chapters for every selected video without touching YouTube's descriptions;
 * apply — insert each approved list into the description that is live on YouTube, leaving the rest
 * of that description exactly as it is. Stops cleanly on quota or auth problems; videos already
 * done are skipped on the next run.
 */
export default function BatchChaptersPanel<N extends MindMapNode>({ items, vn, language, ytConnected, onClose, onSaveTranscript, onApplied, hidden = false, onMinimize, onBusyChange }: Props<N>) {
    const withState = useMemo(() => items.map(it => ({ ...it, existing: chapterState(it.video.youtubeDescription || '') })), [items]);
    // Default selection: every video that has no timestamp list yet.
    const [selected, setSelected] = useState<Set<string>>(() => new Set(withState.filter(x => x.existing === 'none').map(x => x.video.youtubeVideoId)));
    const [status, setStatus] = useState<Record<string, RowStatus>>({});
    const [phase, setPhase] = useState<'select' | 'previewing' | 'review' | 'applying' | 'done'>('select');
    const [stopped, setStopped] = useState<'' | 'quota' | 'auth'>('');
    const cancel = useRef(false);

    const setRow = (id: string, s: RowStatus) => setStatus(prev => ({ ...prev, [id]: s }));
    const toggle = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const busy = phase === 'previewing' || phase === 'applying';
    useEffect(() => { onBusyChange?.(busy); }, [busy]); // eslint-disable-line react-hooks/exhaustive-deps

    const pool = async (ids: string[], work: (x: (typeof withState)[number]) => Promise<void>) => {
        cancel.current = false;
        let cursor = 0;
        const worker = async () => {
            while (cursor < ids.length && !cancel.current) {
                const x = withState.find(v => v.video.youtubeVideoId === ids[cursor++]);
                if (x) await work(x);
            }
        };
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker));
    };

    const halt = (why: 'quota' | 'auth') => { cancel.current = true; setStopped(why); };

    const runPreview = async () => {
        setStopped('');
        setPhase('previewing');
        const ids = [...selected].filter(id => status[id]?.kind !== 'applied');
        await pool(ids, async ({ video, node, existing }) => {
            const id = video.youtubeVideoId;
            if (existing === 'own') { setRow(id, { kind: 'skipped', reason: 'own_list' }); return; }
            setRow(id, { kind: 'working' });
            try {
                let timed = video.youtubeTimedTranscript || '';
                if (!timed) {
                    const r = await fetchYouTubeTranscript(id);
                    if ('text' in r) {
                        timed = r.timed;
                        await onSaveTranscript(node, video, video.youtubeTranscript || r.text, r.timed);
                    } else if (r.error === 'no_captions') { setRow(id, { kind: 'skipped', reason: 'no_captions' }); return; }
                    else if (r.error === 'auth_expired') { setRow(id, { kind: 'idle' }); halt('auth'); return; }
                    else if (/quota/i.test(r.error)) { setRow(id, { kind: 'idle' }); halt('quota'); return; }
                    else { setRow(id, { kind: 'failed', message: r.error }); return; }
                }
                const chapters = await generateVideoChapters(timed, node.title || video.youtubeTitle || '', language);
                setRow(id, chapters.length ? { kind: 'ready', chapters } : { kind: 'skipped', reason: 'too_short' });
            } catch (e: any) {
                setRow(id, { kind: 'failed', message: e?.message || 'error' });
            }
        });
        setPhase('review');
    };

    const runApply = async () => {
        setStopped('');
        setPhase('applying');
        const ids = [...selected].filter(id => status[id]?.kind === 'ready');
        await pool(ids, async ({ video, node }) => {
            const id = video.youtubeVideoId;
            const s = status[id];
            if (s?.kind !== 'ready') return;
            setRow(id, { kind: 'working' });
            try {
                // Merge into what is live on YouTube right now, not into a cached copy.
                const live = await fetchYouTubeVideoSnippet(id);
                if (!live) { setRow(id, { kind: 'failed', message: vn ? 'Không đọc được mô tả hiện tại' : 'Could not read the current description' }); return; }
                const merged = mergeChaptersIntoDescription(live.description, formatChaptersForDescription(s.chapters, vn));
                if (merged === null) {
                    setRow(id, { kind: 'skipped', reason: chapterState(live.description) === 'own' ? 'own_list' : 'too_long' });
                    return;
                }
                await updateYouTubeVideo(id, { description: merged });
                await onApplied(node, video, merged);
                setRow(id, { kind: 'applied' });
            } catch (e: any) {
                const msg = String(e?.message || 'error');
                if (/quota/i.test(msg)) { setRow(id, s); halt('quota'); return; }
                if (/not_connected|auth|401/i.test(msg)) { setRow(id, s); halt('auth'); return; }
                setRow(id, { kind: 'failed', message: msg });
            }
        });
        setPhase('done');
    };

    const counts = useMemo(() => {
        const c = { ready: 0, applied: 0, skipped: 0, failed: 0 };
        for (const id of selected) { const k = status[id]?.kind; if (k === 'ready' || k === 'applied' || k === 'skipped' || k === 'failed') c[k]++; }
        return c;
    }, [selected, status]);
    const needCaptions = withState.filter(x => selected.has(x.video.youtubeVideoId) && !x.video.youtubeTimedTranscript && x.existing !== 'own').length;

    const reasonText = (r: Extract<RowStatus, { kind: 'skipped' }>['reason']) => ({
        no_captions: vn ? 'Chưa có phụ đề trên YouTube' : 'No captions on YouTube yet',
        too_short: vn ? 'Video quá ngắn để chia chương' : 'Too short for chapters',
        own_list: vn ? 'Mô tả đã có danh sách thời gian tự viết — giữ nguyên' : 'Description already has its own timestamp list — left alone',
        too_long: vn ? 'Mô tả sẽ vượt 5000 ký tự' : 'Description would exceed 5000 characters',
    }[r]);

    return (
        <div className="fixed inset-0 z-[320] bg-black/50 flex items-center justify-center p-4" style={hidden ? { display: 'none' } : undefined} onClick={busy ? onMinimize : onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                        <Clock size={16} className="text-indigo-500" />
                        <h3 className="font-bold text-sm text-gray-800 dark:text-gray-100">{vn ? 'Thêm mốc thời gian hàng loạt' : 'Add chapters in bulk'}</h3>
                    </div>
                    {busy && onMinimize ? (
                        <button onClick={onMinimize} className="flex-shrink-0 px-2.5 py-1 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg">
                            {vn ? 'Chạy nền' : 'Run in background'}
                        </button>
                    ) : (
                        <button onClick={onClose} disabled={busy} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-40"><X size={16} /></button>
                    )}
                </div>

                <div className="px-5 py-2.5 text-[11px] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-slate-800">
                    {vn
                        ? 'Bước 1 tạo danh sách để bạn xem trước, chưa sửa gì trên YouTube. Bước 2 chèn danh sách vào mô tả đang có, các phần khác của mô tả giữ nguyên.'
                        : 'Step 1 builds the lists for you to review; nothing on YouTube changes. Step 2 inserts each list into the existing description and leaves the rest untouched.'}
                    {phase === 'select' && needCaptions > 0 && (
                        <span className="block mt-1 text-amber-600 dark:text-amber-400">
                            {vn
                                ? `${needCaptions} video cần tải phụ đề (≈250 đơn vị quota mỗi video; hạn mức 10.000/ngày). Hết quota thì dừng, hôm sau chạy tiếp phần còn lại.`
                                : `${needCaptions} videos need their captions fetched (≈250 quota units each; 10,000/day). The run stops when quota runs out and can be resumed the next day.`}
                        </span>
                    )}
                </div>

                <div className="px-5 py-2 flex items-center gap-3 text-[11px] border-b border-gray-100 dark:border-slate-800">
                    <button disabled={busy} onClick={() => setSelected(new Set(withState.filter(x => x.existing !== 'own').map(x => x.video.youtubeVideoId)))} className="font-semibold text-indigo-500 hover:underline disabled:opacity-40">{vn ? 'Chọn tất cả' : 'Select all'}</button>
                    <button disabled={busy} onClick={() => setSelected(new Set(withState.filter(x => x.existing === 'none').map(x => x.video.youtubeVideoId)))} className="font-semibold text-indigo-500 hover:underline disabled:opacity-40">{vn ? 'Chỉ video chưa có' : 'Only videos without'}</button>
                    <button disabled={busy} onClick={() => setSelected(new Set())} className="font-semibold text-gray-400 hover:underline disabled:opacity-40">{vn ? 'Bỏ chọn' : 'None'}</button>
                    <span className="ml-auto text-gray-400">{vn ? `Đã chọn ${selected.size}/${withState.length}` : `${selected.size}/${withState.length} selected`}</span>
                </div>

                <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
                    {withState.map(({ video, node, existing }) => {
                        const id = video.youtubeVideoId;
                        const s: RowStatus = status[id] ?? { kind: 'idle' };
                        const isSel = selected.has(id);
                        return (
                            <div key={id} className={`rounded-xl border px-3 py-2 ${isSel ? 'border-indigo-200 dark:border-indigo-800 bg-indigo-50/40 dark:bg-indigo-900/10' : 'border-gray-100 dark:border-slate-800'}`}>
                                <label className="flex items-start gap-2.5 cursor-pointer">
                                    <input type="checkbox" className="mt-0.5 accent-indigo-500" checked={isSel} disabled={busy || s.kind === 'applied'} onChange={() => toggle(id)} />
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{video.youtubeTitle || node.title || id}</span>
                                        <span className="block text-[10px] text-gray-400">
                                            {existing === 'ours' ? (vn ? 'Đã có mốc thời gian — sẽ thay bằng danh sách mới' : 'Has chapters — will be replaced')
                                                : existing === 'own' ? (vn ? 'Đã có danh sách tự viết — không đụng tới' : 'Has its own list — not touched')
                                                : (vn ? 'Chưa có mốc thời gian' : 'No chapters yet')}
                                            {!video.youtubeTimedTranscript && existing !== 'own' && (vn ? ' · cần tải phụ đề' : ' · needs captions')}
                                        </span>
                                    </span>
                                    <span className="flex-shrink-0 text-[10px] font-semibold">
                                        {s.kind === 'working' && <Loader2 size={13} className="animate-spin text-indigo-500" />}
                                        {s.kind === 'ready' && <span className="text-indigo-500">{vn ? `${s.chapters.length} chương` : `${s.chapters.length} chapters`}</span>}
                                        {s.kind === 'applied' && <span className="text-emerald-600 flex items-center gap-1"><Check size={12} />{vn ? 'Đã cập nhật' : 'Updated'}</span>}
                                        {s.kind === 'skipped' && <span className="text-gray-400">{vn ? 'Bỏ qua' : 'Skipped'}</span>}
                                        {s.kind === 'failed' && <span className="text-red-500 flex items-center gap-1"><AlertCircle size={12} />{vn ? 'Lỗi' : 'Failed'}</span>}
                                    </span>
                                </label>
                                {s.kind === 'ready' && (
                                    <pre className="mt-1.5 ml-6 text-[11px] leading-relaxed text-gray-600 dark:text-gray-300 whitespace-pre-wrap font-sans">{formatChaptersForDescription(s.chapters, vn).split('\n').slice(1).join('\n')}</pre>
                                )}
                                {s.kind === 'skipped' && <p className="mt-1 ml-6 text-[10px] text-gray-400">{reasonText(s.reason)}</p>}
                                {s.kind === 'failed' && <p className="mt-1 ml-6 text-[10px] text-red-400 break-words">{s.message}</p>}
                            </div>
                        );
                    })}
                    {withState.length === 0 && <p className="text-center text-xs text-gray-400 py-8">{vn ? 'Không có video nào.' : 'No videos.'}</p>}
                </div>

                {stopped && (
                    <div className="px-5 py-2 text-[11px] text-amber-600 dark:text-amber-400 border-t border-gray-100 dark:border-slate-800">
                        {stopped === 'quota'
                            ? (vn ? 'Đã dừng: hết quota YouTube API hôm nay. Các video đã xong được giữ nguyên; mở lại vào hôm sau để chạy tiếp.' : 'Stopped: YouTube API quota is used up for today. Finished videos are kept; run again tomorrow for the rest.')
                            : (vn ? 'Đã dừng: phiên YouTube hết hạn. Kết nối lại YouTube rồi chạy tiếp.' : 'Stopped: the YouTube session expired. Reconnect YouTube and run again.')}
                    </div>
                )}

                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 dark:border-slate-800">
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        {(counts.ready + counts.applied + counts.skipped + counts.failed) > 0 && (vn
                            ? `Sẵn sàng ${counts.ready} · đã cập nhật ${counts.applied} · bỏ qua ${counts.skipped} · lỗi ${counts.failed}`
                            : `Ready ${counts.ready} · updated ${counts.applied} · skipped ${counts.skipped} · failed ${counts.failed}`)}
                        {!ytConnected && <span className="text-amber-600 dark:text-amber-400">{vn ? 'Chưa kết nối YouTube.' : 'YouTube is not connected.'}</span>}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={runPreview}
                            disabled={busy || !ytConnected || selected.size === 0}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-40 flex items-center gap-1.5"
                        >
                            {phase === 'previewing' && <Loader2 size={12} className="animate-spin" />}
                            {vn ? '1. Tạo xem trước' : '1. Build preview'}
                        </button>
                        <button
                            onClick={runApply}
                            disabled={busy || !ytConnected || counts.ready === 0}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-40 flex items-center gap-1.5"
                        >
                            {phase === 'applying' && <Loader2 size={12} className="animate-spin" />}
                            {vn ? `2. Áp dụng lên YouTube (${counts.ready})` : `2. Apply to YouTube (${counts.ready})`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
